import { readPagedRows } from '@/shared/utils/spring-api';
function normalizePermissionItem(item, idx) {
    if (typeof item === 'string') {
        return { id: item, action: item, description: item, active: true };
    }
    if (!item || typeof item !== 'object')
        return null;
    const o = item;
    const action = String(o.action ?? o.name ?? o.permission ?? o.authority ?? '').trim();
    const id = o.id != null ? o.id : action || idx;
    if (!action && o.id == null)
        return null;
    const description = typeof o.description === 'string'
        ? o.description
        : o.description == null
            ? ''
            : typeof o.label === 'string'
                ? o.label
                : action;
    return {
        id,
        action: action || undefined,
        description,
        active: o.active !== false,
        createdBy: typeof o.createdBy === 'string' ? o.createdBy : undefined,
        createdDate: typeof o.createdDate === 'string' ? new Date(o.createdDate) : o.createdDate === null ? null : undefined,
        lastModifiedBy: typeof o.lastModifiedBy === 'string' ? o.lastModifiedBy : undefined,
        lastModifiedDate: typeof o.lastModifiedDate === 'string'
            ? new Date(o.lastModifiedDate)
            : o.lastModifiedDate === null
                ? null
                : undefined,
    };
}
/** Normalize IAM GET `user/api/permissions` (array or wrapped list). */
export function normalizeIamPermissionsList(data) {
    if (data == null)
        return [];
    let list = data;
    if (typeof list === 'object' && list !== null && 'data' in list) {
        const inner = list.data;
        if (Array.isArray(inner))
            list = inner;
        else if (inner && typeof inner === 'object' && 'data' in inner) {
            const deeper = inner.data;
            if (Array.isArray(deeper))
                list = deeper;
        }
    }
    if (!Array.isArray(list) && typeof list === 'object' && list !== null && 'content' in list) {
        list = list.content ?? [];
    }
    if (!Array.isArray(list))
        return [];
    return list
        .map((item, idx) => normalizePermissionItem(item, idx))
        .filter((p) => p !== null);
}
function comparePermissionValues(a, b) {
    if (a == null && b == null)
        return 0;
    if (a == null)
        return 1;
    if (b == null)
        return -1;
    if (typeof a === 'number' && typeof b === 'number')
        return a - b;
    return String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
}
function sortPermissions(list, sort) {
    if (!sort?.trim())
        return list;
    const [fieldRaw, dirRaw] = sort.split(',');
    const field = fieldRaw?.trim() || 'id';
    const desc = (dirRaw ?? 'asc').trim().toLowerCase() === 'desc';
    const copy = [...list];
    copy.sort((a, b) => {
        const av = a[field];
        const bv = b[field];
        const cmp = comparePermissionValues(av, bv);
        return desc ? -cmp : cmp;
    });
    return copy;
}
function filterPermissions(list, filter) {
    const q = filter?.trim().toLowerCase();
    if (!q)
        return list;
    return list.filter((item) => {
        const action = String(item.action ?? '').toLowerCase();
        const description = String(item.description ?? '').toLowerCase();
        return action.includes(q) || description.includes(q);
    });
}
export function readIamPermissionsPage(data, headers, page, size, sort, filter) {
    const normalized = filterPermissions(normalizeIamPermissionsList(data), filter);
    const sorted = sortPermissions(normalized, sort);
    if (Array.isArray(data)) {
        const total = sorted.length;
        const p = page ?? 0;
        const s = size ?? 10;
        const start = p * s;
        return { rows: sorted.slice(start, start + s), total };
    }
    const paged = readPagedRows(data, headers);
    const rows = sortPermissions(filterPermissions(paged.rows.map((dto, idx) => normalizePermissionItem(dto, idx)).filter((p) => p !== null), filter), sort);
    return {
        rows,
        total: filter?.trim() ? rows.length : paged.total,
    };
}
export function fromIamPermission(dto) {
    return normalizePermissionItem(dto, 0) ?? { action: '', description: '', active: true };
}
/** Map UI form → IAM POST/PUT body. */
export function toIamPermissionPayload(entity) {
    const action = String(entity.action ?? '').trim();
    const descriptionRaw = entity.description;
    const description = descriptionRaw == null || String(descriptionRaw).trim() === '' ? null : String(descriptionRaw).trim();
    return {
        ...(entity.id != null && entity.id !== '' ? { id: entity.id } : {}),
        action,
        active: entity.active !== false,
        description,
    };
}
